import { Box, Button, Container, FloatingIndicator, Group, Scroller, Text } from "@mantine/core";
import { useMemo, useState, type FC } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import Icon from "src/components/common/Icon";
import type { ILayoutItemContext, ILayoutLink } from "src/contexts/layouts/LayoutContext";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import { filterLayoutLinksByPermission } from "src/utilities/hasLayoutLinkPermission";

import styles from "./SubMenu.module.scss";

/**
 * Defines the inputs for this navigation presentation.
 */
interface ISubMenuProps {
    color: string;
    backgroundColor: string;
    indicatorColor: string;
    title?: string;
    menuItems?: ILayoutLink[];
    context?: ILayoutItemContext;
}

/**
 * Defines the inputs for this navigation presentation.
 */
interface ISubMenuInternalProps {
    menuItems: ILayoutLink[];
    context?: ILayoutItemContext;
    backgroundColor: string;
    indicatorColor: string;
    color: string;
}

/**
 * Renders submenu links filtered against the supplied entity context.
 *
 * Postconditions: The first allowed path prefix selects the indicator. Actions and router links
 * retain distinct rendering; existing memo dependencies control when navigation is rebuilt.
 */
const SubMenuInternal: FC<ISubMenuInternalProps> = ({
    menuItems,
    backgroundColor,
    indicatorColor,
    color,
    context
}) => {
    const permissions = useCurrentUserPermissions();
    const location = useLocation();

    const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
    const [controlsRefs, setControlsRefs] = useState<Record<string, HTMLAnchorElement | null>>({});

    const navigation = useMemo(
        () => {
            /**
             * Creates a callback that requests an updated href-to-element map for the active indicator.
             */
            const setControlRef = (href: string) => (node: HTMLAnchorElement) => {
                setControlsRefs(current => {
                    const newRefs = { ...current };
                    newRefs[href] = node;
                    return newRefs;
                });
            };

            const allowedMenuItems = filterLayoutLinksByPermission(permissions, menuItems, context);

            const items: React.ReactNode[] = [];

            for (const menuItem of allowedMenuItems) {
                if (menuItem.actionFn) {
                    items.push(
                        <Button
                            key={menuItem.href}
                            color={backgroundColor}
                            c={color}
                            radius={0}
                            size="md"
                            ref={setControlRef(menuItem.href) as any}
                            onClick={menuItem.actionFn}
                            leftSection={
                                menuItem.label && menuItem.icon ? (
                                    <Icon icon={menuItem.icon} />
                                ) : undefined
                            }
                            style={menuItem.style}
                            className={menuItem.className}
                        >
                            {menuItem.label ? (
                                menuItem.label
                            ) : menuItem.icon ? (
                                <Icon icon={menuItem.icon} />
                            ) : null}
                        </Button>
                    );

                    continue;
                }

                items.push(
                    <Button
                        key={menuItem.href}
                        color={backgroundColor}
                        c={color}
                        radius={0}
                        ref={setControlRef(menuItem.href)}
                        component={RouterLink}
                        to={menuItem.href}
                        target={menuItem.newTab ? "_blank" : undefined}
                        leftSection={
                            menuItem.label && menuItem.icon ? <Icon icon={menuItem.icon} /> : undefined
                        }
                        style={menuItem.style}
                        className={menuItem.className}
                    >
                        {menuItem.label ? (
                            menuItem.label
                        ) : menuItem.icon ? (
                            <Icon icon={menuItem.icon} />
                        ) : null}
                    </Button>
                );
            }

            return {
                items,
                allowedMenuItems
            };
        },
        [menuItems, permissions, setControlsRefs, backgroundColor, context, location.pathname]
    );

    const selectedHref = useMemo(
        () => {
            return navigation.allowedMenuItems.find(menuItem =>
                location.pathname.startsWith(menuItem.href)
            )?.href;
        },
        [navigation, location.pathname]
    );

    return (
        <Scroller
            draggable
            edgeGradientColor={backgroundColor}
            styles={{
                content: {
                    minWidth: "100%"
                }
            }}
        >
            <Group
                gap={0}
                ref={setRootRef}
                pos="relative"
                wrap="nowrap"
                w="100%"
            >
                {navigation.items}
                <FloatingIndicator
                    target={controlsRefs[selectedHref]}
                    parent={rootRef}
                    className={styles.indicator}
                    style={{ "--indicator-color": indicatorColor }}
                />
            </Group>
        </Scroller>
    );
};

/**
 * Renders the submenu band and optional title, adding navigation when input items exist.
 */
const SubMenu: FC<ISubMenuProps> = ({
    menuItems,
    color,
    backgroundColor,
    indicatorColor,
    title,
    context
}) => {
    const hasMenuItems = menuItems && menuItems.length > 0;

    return (
        <Box
            bg={backgroundColor}
            c={color}
        >
            <Container size="xl">
                {title && (
                    <Group
                        justify="center"
                        py={4}
                    >
                        <Text fw="bold">{title}</Text>
                    </Group>
                )}
                {hasMenuItems && (
                    <SubMenuInternal
                        menuItems={menuItems}
                        backgroundColor={backgroundColor}
                        indicatorColor={indicatorColor}
                        color={color}
                        context={context}
                    />
                )}
            </Container>
        </Box>
    );
};

export default SubMenu;
